import { AddCandidateToListDialog } from "@components/candidates/AddCandidateToListDialog";
import { CandidateReferenceBadge } from "@components/candidates/CandidateReferenceBadge";
import { TALENT_POOL_QUERY } from "@lib/graphql/queries";
import { useBackgroundTaskCompletion } from "@lib/hooks/useBackgroundTaskCompletion";
import { useChatDataVersion } from "@lib/hooks/useChatDataVersion";
import { ArrowLeft, Plus } from "lucide-react";
import { useCallback, useState } from "react";
import { Link, useParams } from "react-router";
import { useQuery } from "urql";

interface Membership {
  id: string;
  candidate: {
    id: string;
    referenceNumber: number | null;
    fullName: string;
    email: string | null;
    currentRoleTitle: string | null;
    currentCompany: string | null;
    location: string | null;
  };
  addedBy: { id: string; name: string };
  relevanceNotes: string | null;
  createdAt: string;
}

interface TalentPool {
  id: string;
  name: string;
  description: string | null;
  candidateCount: number;
  campaign: { id: string; title: string; status: string } | null;
  createdBy: { id: string; name: string };
  memberships: Membership[];
  createdAt: string;
}

export function TalentPoolDetail() {
  const { id } = useParams();
  const [{ data, fetching }, reexecute] = useQuery({ query: TALENT_POOL_QUERY, variables: { id } });
  const refresh = useCallback(() => reexecute({ requestPolicy: "network-only" }), [reexecute]);
  useChatDataVersion(refresh);
  useBackgroundTaskCompletion("linkedin_import", refresh);
  const [adding, setAdding] = useState(false);

  const pool: TalentPool | undefined = data?.talentPool;

  if (fetching) return <p className="text-sm text-text-muted">Loading...</p>;
  if (!pool) return <p className="text-sm text-text-muted">Talent pool not found</p>;

  return (
    <div>
      <Link
        to="/talent-pools"
        className="inline-flex items-center gap-1 text-sm text-text-muted hover:text-text-primary"
      >
        <ArrowLeft size={14} /> Back to Talent Pools
      </Link>

      <div className="mt-4 rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl font-bold tracking-tight text-text-primary">{pool.name}</h1>
            {pool.description && (
              <p className="mt-1 text-sm text-text-secondary">{pool.description}</p>
            )}
            <div className="mt-3 flex gap-4 text-sm text-text-muted">
              <span>
                {pool.candidateCount} candidate{pool.candidateCount !== 1 ? "s" : ""}
              </span>
              {pool.campaign && (
                <span>
                  Campaign:{" "}
                  <Link
                    to={`/campaigns/${pool.campaign.id}`}
                    className="text-accent hover:text-accent-hover"
                  >
                    {pool.campaign.title}
                  </Link>
                </span>
              )}
              <span>Created by {pool.createdBy.name}</span>
              <span>{new Date(pool.createdAt).toLocaleDateString()}</span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setAdding((v) => !v)}
            className="flex items-center gap-1 rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-hover"
          >
            <Plus size={12} /> Add candidate
          </button>
        </div>
      </div>

      {adding && (
        <div className="mt-4">
          <AddCandidateToListDialog
            target={{ kind: "talent_pool", talentPoolId: pool.id }}
            heading={`Add candidate to ${pool.name}`}
            onDone={() => {
              setAdding(false);
              refresh();
            }}
            onCancel={() => setAdding(false)}
          />
        </div>
      )}

      {pool.campaign ? (
        <div className="mt-4 rounded-lg border border-accent/20 bg-accent/5 px-4 py-3">
          <p className="text-sm text-text-secondary">
            <span className="font-medium text-accent">Campaign Pool</span> — This pool is linked to{" "}
            <Link
              to={`/campaigns/${pool.campaign.id}`}
              className="font-medium text-accent hover:text-accent-hover"
            >
              {pool.campaign.title}
            </Link>
            . Candidates here are passive prospects captured for future reference — they are not
            part of the active campaign pipeline.
          </p>
        </div>
      ) : (
        <div className="mt-4 rounded-lg border border-border bg-card-alt px-4 py-3">
          <p className="text-sm text-text-secondary">
            <span className="font-medium text-text-primary">Standalone Pool</span> — Candidates here
            are organised for future searches and are not part of any active campaign.
          </p>
        </div>
      )}

      {pool.memberships.length === 0 ? (
        <div className="mt-4 rounded-lg border-2 border-dashed border-border-dashed p-8 text-center">
          <p className="text-sm text-text-muted">
            No candidates in this pool yet. Use Add candidate to search or import.
          </p>
        </div>
      ) : (
        <div className="mt-4 overflow-hidden rounded-lg bg-card shadow-sm ring-1 ring-ring">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-card-alt">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                  Candidate
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                  Role / Company
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                  Location
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                  Relevance Notes
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                  Added By
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {pool.memberships.map((m) => (
                <tr key={m.id} className="hover:bg-card-hover">
                  <td className="px-4 py-3">
                    {typeof m.candidate.referenceNumber === "number" && (
                      <>
                        <CandidateReferenceBadge
                          referenceNumber={m.candidate.referenceNumber}
                        />{" "}
                      </>
                    )}
                    <Link
                      to={`/candidates/${m.candidate.id}`}
                      className="font-medium text-accent hover:text-accent-hover"
                    >
                      {m.candidate.fullName}
                    </Link>
                    {m.candidate.email && (
                      <p className="text-xs text-text-faint">{m.candidate.email}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-text-secondary">
                    {[m.candidate.currentRoleTitle, m.candidate.currentCompany]
                      .filter(Boolean)
                      .join(" at ") || "\u2014"}
                  </td>
                  <td className="px-4 py-3 text-sm text-text-secondary">
                    {m.candidate.location || "\u2014"}
                  </td>
                  <td className="px-4 py-3 text-sm text-text-secondary">
                    {m.relevanceNotes || "\u2014"}
                  </td>
                  <td className="px-4 py-3 text-sm text-text-secondary">{m.addedBy.name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
